import { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { filtersToQueryString } from "@redotech/redo-model/conversation-filters/conversation-filter-query";
import { ConversationFiltersV3 } from "@redotech/redo-model/conversation-filters/conversation-filters";
import { SortableConversationTableColumn } from "@redotech/redo-model/tables/conversations-table-sort";
import { TableSort } from "@redotech/redo-model/tables/table";
import { GetUser } from "@redotech/redo-model/user";
import { YieldType } from "@redotech/redo-web/table";
import { stringHash } from "@redotech/util/string";
import { GenericConversationFetcher } from "./generic-conversation-fetcher";

export interface GenericConversationFetcherResult<T> {
  data: T[];
  pageNext: string | undefined;
}

export abstract class GenericCachedConversationFetcher<
  T extends { _id: string; messages: { user?: string | null }[] },
> extends GenericConversationFetcher<T> {
  constructor(
    private cache: GenericConversationFetcherCache<T>,
    client: RedoMerchantClient,
    userCache: Map<string, GetUser>,
    onConversationsLoaded?: (() => void) | undefined,
  ) {
    super(client, userCache, onConversationsLoaded);
  }

  protected abstract getConversationsWithRetry(params: {
    pageContinue: string | undefined;
    pageStop?: string;
    pageSize: number;
    filters: ConversationFiltersV3 | undefined;
    sort: TableSort<SortableConversationTableColumn> | undefined;
    signal?: AbortSignal;
  }): Promise<GenericConversationFetcherResult<T>>;

  invalidateCache() {
    this.cache.invalidate();
  }

  override async *data(
    primaryFilter: string,
    filters: ConversationFiltersV3,
    search: string | undefined,
    sort: TableSort<SortableConversationTableColumn> | undefined,
    pageSize = 50,
    pageNumber?: number,
    signal?: AbortSignal,
    passThroughValues?: ConversationFiltersV3,
  ): AsyncIterator<YieldType<T>> {
    let cursor: string | undefined;
    let highestLoadedPage = 0;

    const refresh = async (
      pageStop: string | undefined,
      toPage: number | undefined,
      signal: AbortSignal | undefined,
    ) => {
      const tempCache = new GenericConversationFetcherCache<T>();
      let items: T[] = [];
      let page = 1;
      for (let refreshCursor: string | undefined; ; ) {
        try {
          // We don't need to request the next page if we've already made it to the point of the page stop
          if (!!refreshCursor && refreshCursor === pageStop) {
            break;
          }
          const result = await this.getConversationsWithRetry({
            pageContinue: refreshCursor,
            pageStop,
            pageSize,
            filters: passThroughValues,
            sort: passThroughValues?.sort || sort,
            signal,
          });

          tempCache.set(
            refreshCursor,
            pageSize,
            passThroughValues,
            sort,
            result,
          );

          const { data, pageNext } = result;

          this.updateConversationsMap(data);
          await this.fetchUsers(data, signal);
          if (data && data.length > 0) {
            items = [...items, ...data];
          }
          if (page === toPage) {
            break;
          }
          ++page;
          if (pageNext) {
            refreshCursor = pageNext;
          } else {
            break;
          }
        } catch (e: any) {
          this.cache.invalidate();
          if (e.code === "ERR_CANCELED") {
            return [];
          }
          throw e;
        }
      }
      this.cache = tempCache;
      return items;
    };
    for (let pageContinue: string | undefined; ; ) {
      try {
        let result: GenericConversationFetcherResult<T>;

        const cachedResult = this.cache.get(
          pageContinue,
          pageSize,
          passThroughValues,
          sort,
        );
        if (cachedResult) {
          result = cachedResult;
        } else {
          result = await this.getConversationsWithRetry({
            pageContinue,
            pageSize,
            filters: passThroughValues,
            sort: passThroughValues?.sort || sort,
            signal,
          });
          this.cache.set(
            pageContinue,
            pageSize,
            passThroughValues,
            sort,
            result,
          );
        }

        const { data, pageNext } = result;

        this.updateConversationsMap(data);
        await this.fetchUsers(data, signal);

        cursor = pageNext;
        highestLoadedPage++;
        this.onConversationsLoaded?.();
        yield {
          items: data,
          refresh: async (signal) => refresh(cursor, highestLoadedPage, signal),
        };
        if (pageNext === undefined) {
          break;
        }
        pageContinue = pageNext;
      } catch (e: any) {
        if (e.code !== "ERR_CANCELED") {
          throw e;
        }
        return {
          items: [],
          refresh: async (signal) => refresh(cursor, highestLoadedPage, signal),
          aborted: true,
        } as YieldType<T>;
      }
    }
  }
}

function getCacheKey(
  pageContinue: string | undefined,
  pageSize: number,
  passThroughValues: ConversationFiltersV3 | undefined,
  sort: TableSort<SortableConversationTableColumn> | undefined,
) {
  // This is the same way we construct the query string for the request to the backend. Better than doing a JSON.stringify as it is not guaranteed to order the items the same way
  const v3Filters: ConversationFiltersV3 = Object.assign(
    { advancedFilters: [] },
    passThroughValues,
    sort,
  );
  const query = filtersToQueryString(v3Filters);
  return stringHash(query + pageContinue + pageSize).toString();
}

export class GenericConversationFetcherCache<T> {
  private cache = new Map<string, GenericConversationFetcherResult<T>>();

  set(
    pageContinue: string | undefined,
    pageSize: number,
    passThroughValues: ConversationFiltersV3 | undefined,
    sort: TableSort<SortableConversationTableColumn> | undefined,
    result: GenericConversationFetcherResult<T>,
  ): void {
    const hash = getCacheKey(pageContinue, pageSize, passThroughValues, sort);
    this.cache.set(hash, result);
  }

  get(
    pageContinue: string | undefined,
    pageSize: number,
    passThroughValues: ConversationFiltersV3 | undefined,
    sort: TableSort<SortableConversationTableColumn> | undefined,
  ): GenericConversationFetcherResult<T> | undefined {
    const hash = getCacheKey(pageContinue, pageSize, passThroughValues, sort);
    return this.cache.get(hash);
  }

  invalidate() {
    this.cache.clear();
  }
}
